import React, { useState, useRef, useEffect, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import HTMLFlipBook from "react-pageflip";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Maximize2, Minimize2, Loader2 } from "lucide-react";
import { trackBookDownload } from "../lib/trackBookDownload";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BOOK_PDF =
  "https://customer-assets.emergentagent.com/job_ya-digital/artifacts/riplp2t7_Resumen_por_capitulos_Capitalismo_de_Abundancia.pdf";

const FRONT_COVER = "/book-cover.jpg";
const BACK_COVER = "/book-back-cover.jpg";

// Hoja que renderiza una imagen (tapa/contratapa).
// Usa data-density="hard" para que react-pageflip la trate como tapa rígida.
const ImageSheet = React.forwardRef(({ src, alt, width, height, label }, ref) => (
  <div
    ref={ref}
    className="bg-white shadow-xl"
    data-density="hard"
    style={{ width, height }}
  >
    <div className="relative w-full h-full overflow-hidden bg-white">
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="w-full h-full object-cover select-none"
      />
      {label && (
        <div className="absolute bottom-2 right-3 text-[10px] text-white/70 font-headings uppercase tracking-widest">
          {label}
        </div>
      )}
    </div>
  </div>
));
ImageSheet.displayName = "ImageSheet";

// Página individual del flipbook (forwardRef requerido por react-pageflip)
const Sheet = React.forwardRef(({ pageNumber, width, height, label }, ref) => {
  return (
    <div ref={ref} className="bg-white shadow-xl" data-density="hard">
      <div className="relative w-full h-full overflow-hidden">
        {pageNumber ? (
          <Page
            pageNumber={pageNumber}
            width={width}
            height={height}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            loading={
              <div className="flex items-center justify-center h-full text-gray-400">
                <Loader2 className="animate-spin" size={20} />
              </div>
            }
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm font-headings uppercase tracking-widest">
            {label}
          </div>
        )}
        <div className="absolute bottom-2 right-3 text-[10px] text-gray-400 font-headings tabular-nums">
          {pageNumber}
        </div>
      </div>
    </div>
  );
});
Sheet.displayName = "Sheet";

// (Cover y BackCover custom eliminados — usamos las páginas reales del PDF)

export const BookReader = ({ open, onClose, initialPage = null }) => {
  const flipRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [pageIndex, setPageIndex] = useState(0); // current sheet index
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);

  // Responsive size: el libro ocupa el alto disponible y usa 2 páginas en desktop
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (!open) return;
    const compute = () => {
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const mobile = vw < 768;
      setIsMobile(mobile);
      const targetH = Math.min(vh * 0.82, 900);
      const singleW = targetH / 1.414; // proporción A4
      const totalW = mobile ? singleW : singleW * 2;
      const maxW = vw * 0.92;
      if (totalW > maxW) {
        const scale = maxW / totalW;
        setContainerSize({
          width: Math.floor(singleW * scale),
          height: Math.floor(targetH * scale),
        });
      } else {
        setContainerSize({ width: Math.floor(singleW), height: Math.floor(targetH) });
      }
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [open]);

  // Saltar a página inicial cuando se abre (offset de tapa = +1)
  useEffect(() => {
    if (!open || !initialPage || !numPages) return;
    const target = Math.max(1, Math.min(initialPage, numPages));
    setTimeout(() => {
      try {
        // sheet 0 = tapa, sheet 1 = PDF página 1, etc.
        flipRef.current?.pageFlip()?.flip(target);
      } catch (e) { /* */ }
    }, 400);
  }, [open, initialPage, numPages]);

  // Bloquear scroll del body
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const onLoadSuccess = useCallback(({ numPages }) => setNumPages(numPages), []);

  const goPrev = () => flipRef.current?.pageFlip()?.flipPrev();
  const goNext = () => flipRef.current?.pageFlip()?.flipNext();
  const onFlip = useCallback((e) => setPageIndex(e.data), []);

  if (!open) return null;

  const currentReal = Math.max(0, pageIndex);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[200] bg-[#0A0A0A] flex flex-col"
        data-testid="book-reader"
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 sm:px-8 py-4 text-white border-b border-white/10">
          <div className="font-headings text-xs uppercase tracking-[0.25em] text-white/70">
            <span className="text-[#F5B400]">Capitalismo de Abundancia</span>
            {numPages > 0 && (
              <span className="ml-3 tabular-nums">
                {pageIndex === 0
                  ? "Tapa"
                  : pageIndex >= numPages + 1
                  ? "Contratapa"
                  : `Página ${pageIndex} / ${numPages}`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <a
              href={BOOK_PDF}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackBookDownload("reader-btn")}
              className="hidden sm:inline-flex text-xs font-headings uppercase tracking-wide text-white/70 hover:text-[#F5B400]"
              data-testid="book-reader-download"
            >
              Descargar PDF
            </a>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
              aria-label="Cerrar"
              data-testid="book-reader-close"
            >
              <X size={28} />
            </button>
          </div>
        </div>

        {/* Book area */}
        <div
          ref={containerRef}
          className="flex-1 flex items-center justify-center relative overflow-hidden"
        >
          {/* Prev arrow */}
          <button
            onClick={goPrev}
            className="absolute left-2 sm:left-6 z-10 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/10 hover:bg-[#F5B400] hover:text-[#0A192F] text-white flex items-center justify-center transition-colors backdrop-blur-sm"
            aria-label="Página anterior"
            data-testid="book-reader-prev"
          >
            <ChevronLeft size={28} />
          </button>

          {containerSize.width > 0 && (
            <Document
              file={BOOK_PDF}
              onLoadSuccess={onLoadSuccess}
              loading={
                <div className="text-white/70 flex items-center gap-3">
                  <Loader2 className="animate-spin" size={20} /> Cargando libro…
                </div>
              }
              error={<div className="text-red-300">No se pudo cargar el libro.</div>}
            >
              {numPages > 0 && (
                <HTMLFlipBook
                  width={containerSize.width}
                  height={containerSize.height}
                  size="fixed"
                  minWidth={280}
                  maxWidth={1200}
                  minHeight={400}
                  maxHeight={1400}
                  showCover={true}
                  flippingTime={650}
                  usePortrait={isMobile}
                  startZIndex={20}
                  drawShadow={true}
                  mobileScrollSupport={true}
                  ref={flipRef}
                  onFlip={onFlip}
                  className=""
                  style={{ margin: "0 auto" }}
                  startPage={0}
                  maxShadowOpacity={0.5}
                  showPageCorners={true}
                  disableFlipByClick={false}
                  useMouseEvents={true}
                  swipeDistance={30}
                  clickEventForward={true}
                  autoSize={false}
                >
                  {/* TAPA diseñada (hard cover) */}
                  <ImageSheet
                    key="front-cover"
                    src={FRONT_COVER}
                    alt="Capitalismo de Abundancia — Tapa"
                    width={containerSize.width}
                    height={containerSize.height}
                  />
                  {/* Páginas del PDF */}
                  {Array.from({ length: numPages }, (_, i) => (
                    <Sheet
                      key={i + 1}
                      pageNumber={i + 1}
                      width={containerSize.width}
                      height={containerSize.height}
                    />
                  ))}
                  {/* CONTRATAPA diseñada (hard cover) */}
                  <ImageSheet
                    key="back-cover"
                    src={BACK_COVER}
                    alt="Capitalismo de Abundancia — Contratapa"
                    width={containerSize.width}
                    height={containerSize.height}
                  />
                </HTMLFlipBook>
              )}
            </Document>
          )}

          {/* Next arrow */}
          <button
            onClick={goNext}
            className="absolute right-2 sm:right-6 z-10 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/10 hover:bg-[#F5B400] hover:text-[#0A192F] text-white flex items-center justify-center transition-colors backdrop-blur-sm"
            aria-label="Página siguiente"
            data-testid="book-reader-next"
          >
            <ChevronRight size={28} />
          </button>
        </div>

        {/* Bottom hint */}
        <div className="px-5 sm:px-8 py-3 text-center text-xs text-white/40 font-headings uppercase tracking-[0.2em] border-t border-white/10">
          Tocá las esquinas o usá las flechas para hojear · Pellizcá para zoom
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BookReader;
